import * as React from 'react';
import { useDispatch } from 'react-redux';

import Transcript, { TranscriptProps } from 'blocks/Transcript';
import usePrevious from 'hooks/usePrevious';
import { pushModal } from 'redux/modules/modal';
import { TranscriptStatus } from 'utils/constants';

import AssetsPaneContent from '../../components/AssetsPaneContent';
import { block } from '../../utils';
import CaptionsForm, { CaptionsFormProps } from './CaptionsForm';
import CaptionsModals, { CaptionsModal } from './CaptionsModals';
import TranscriptStatusDisplay from './TranscriptStatusDisplay';

const { useCallback, useEffect, useState } = React;

type PickedFormProps = Pick<CaptionsFormProps, 'captionsEnabled'>;
type PickedTranscriptProps = Pick<
  TranscriptProps,
  | 'onCopyPhraseToTimeline'
  | 'onKeywordClick'
  | 'onPause'
  | 'onPlayPhrase'
  | 'playing'
>;

export interface TranscriptTabProps
  extends PickedFormProps,
    PickedTranscriptProps {
  onModalHide?: () => void;
  onModalShow?: () => void;
  onUploadCaptionsClick?: CaptionsFormProps['onClickCaptionUpload'];
  positionMillis: number;
  syncTranscriptWithPlayback: boolean;
  transcriptStatus: TranscriptStatus;
}

const TranscriptTab: React.SFC<TranscriptTabProps> = ({
  captionsEnabled,
  onCopyPhraseToTimeline,
  onKeywordClick,
  onModalHide,
  onModalShow,
  onPause,
  onPlayPhrase,
  onUploadCaptionsClick,
  playing,
  positionMillis,
  syncTranscriptWithPlayback,
  transcriptStatus,
}) => {
  const [showModal, setShowModal] = useState<CaptionsModal>();
  const prevShowModal = usePrevious(showModal);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!prevShowModal && showModal) {
      onModalShow();
    } else if (prevShowModal && !showModal) {
      onModalHide();
    }
  }, [onModalShow, onModalHide, prevShowModal, showModal]);

  const handleStyleButtonClick = useCallback(() => setShowModal('edit'), [
    setShowModal,
  ]);

  const handleUploadButtonClick = useCallback(() => setShowModal('add'), [
    setShowModal,
  ]);

  const handleDownloadClick = useCallback(() => {
    dispatch(pushModal({ name: 'DownloadProjectCaptions' }));
  }, [dispatch]);

  const handleModalHide = useCallback(() => setShowModal(undefined), [
    setShowModal,
  ]);

  const handleUploadCaptionsClick = useCallback(() => {
    handleUploadButtonClick();
    onUploadCaptionsClick?.();
  }, [handleUploadButtonClick, onUploadCaptionsClick]);

  return (
    <AssetsPaneContent className={block('transcript-tab')}>
      {transcriptStatus !== TranscriptStatus.TRANSCRIPT_STATUS_IN_PROGESS && (
        <CaptionsForm
          captionsEnabled={captionsEnabled}
          onClickCaptionUpload={handleUploadCaptionsClick}
          onStyleButtonClick={handleStyleButtonClick}
          transcriptStatus={transcriptStatus}
          onDownloadClick={handleDownloadClick}
        />
      )}
      {transcriptStatus === TranscriptStatus.TRANSCRIPT_STATUS_AVAILABLE ? (
        <Transcript
          onCopyPhraseToTimeline={onCopyPhraseToTimeline}
          onKeywordClick={onKeywordClick}
          onPause={onPause}
          onPlayPhrase={onPlayPhrase}
          playing={playing}
          position={positionMillis}
          scrollToPhrase={syncTranscriptWithPlayback}
        />
      ) : (
        <TranscriptStatusDisplay
          status={transcriptStatus}
          onClickCaptionUpload={handleUploadCaptionsClick}
        />
      )}

      <CaptionsModals
        onHide={handleModalHide}
        onOpenEditModal={handleStyleButtonClick}
        show={showModal}
        transcriptStatus={transcriptStatus}
        openUploadCaptionsModal={handleUploadButtonClick}
      />
    </AssetsPaneContent>
  );
};

export default TranscriptTab;
